import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CustomSelectComponent } from '../custom-select/custom-select.component';
import { QuickAddSelectComponent } from '../quick-add-select/quick-add-select.component';
import { DocAttachComponent } from '../doc-attach/doc-attach.component';
import { DatePickerComponent } from '../date-picker/date-picker.component';

import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionService } from '@core/services/permission.service';

export interface EmployeeFormSubmit {
  payload: any;
  mode: 'add' | 'edit' | 'view';
  editingId: string;
  selectedTeamId: string;
  pendingDocs: { [docType: string]: File };
  formId?: string;
}

@Component({
  standalone: true,
  selector: 'app-employee-form-drawer',
  imports: [
    CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule,
    CustomSelectComponent, QuickAddSelectComponent, DocAttachComponent, DatePickerComponent,
  ],
  templateUrl: './employee-form-drawer.component.html',
  styleUrls: ['./employee-form-drawer.component.scss'],
})
export class EmployeeFormDrawerComponent {
  private destroyRef = inject(DestroyRef);

  @Input() departments: any[] = [];
  @Input() teams: any[] = [];
  @Input() clients: any[] = [];
  @Input() employees: any[] = [];
  @Input() roles: any[] = [];
  @Input() userRole = 'employee';

  get roleSelectOptions() {
    if (this.roles.length === 0) return [
      { label: 'Employee', value: 'employee' }, { label: 'HR', value: 'hr' }, { label: 'Admin', value: 'admin' },
    ];
    return this.roles.map((r: any) => ({ label: r.displayName || r.name, value: r.name }));
  }

  @Output() submitForm = new EventEmitter<EmployeeFormSubmit>();
  @Output() closed = new EventEmitter<void>();

  showFormDialog = false;
  formMode: 'add' | 'edit' | 'view' = 'add';
  empDrawerTab: 'personal' | 'professional' | 'address' | 'education' | 'documents' = 'personal';
  submitting = false;
  formDirty = false;
  editingId = '';
  formSnapshot = '';

  form: any = this.emptyForm();
  educationRows: any[] = [];
  experienceRows: any[] = [];
  dependentRows: any[] = [];
  healthRows: any[] = [];
  rolesRows: any[] = [];

  managersList: any[] = [];

  empDocs: any[] = [];
  docsLoading = false;
  docUploadType = '';
  docUploading = false;
  reviewDocs: Record<string, string> = {};

  get isView(): boolean { return this.formMode === 'view'; }

  get canEditEmpId(): boolean {
    const r = (this.userRole || '').toLowerCase().replace(/^role_/, '');
    return !this.isView && ['admin', 'hr_admin', 'super_admin'].includes(r);
  }

  get downloadPrefix(): string {
    const id = (this.form?.employeeId || '').trim();
    const name = `${this.form?.firstName || ''} ${this.form?.lastName || ''}`.trim();
    const parts = [id, name].filter(Boolean).join('_');
    return parts ? parts + '_' : '';
  }
  readonly legacyView = false;
  readonly today = new Date();

  submitAttempted = false;
  missingRequired: string[] = [];

  countries: string[] = [];
  presentStates: string[] = [];
  presentCities: string[] = [];
  permanentStates: string[] = [];
  permanentCities: string[] = [];
  postalLookupLoading: { present: boolean; permanent: boolean } = { present: false, permanent: false };
  postalLookupError: { present: string; permanent: string } = { present: '', permanent: '' };

  titles = ['CEO', 'Administration', 'Manager', 'Assistant Manager', 'Team Member'];
  genderOptions = [
    { label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' },
  ];
  statusOptions = [
    { label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' },
  ];
  locationOptions = [
    { label: 'On-site', value: 'On-site' }, { label: 'Remote', value: 'Remote' }, { label: 'Hybrid', value: 'Hybrid' },
  ];
  sourceOfHireOptions = [
    { label: 'Campus Recruitment', value: 'Campus Recruitment' }, { label: 'Job Portal', value: 'Job Portal' },
    { label: 'Employee Referral', value: 'Employee Referral' }, { label: 'Direct Application', value: 'Direct Application' },
    { label: 'Agency', value: 'Agency' }, { label: 'LinkedIn', value: 'LinkedIn' }, { label: 'Other', value: 'Other' },
  ];
  employmentTypeOptions = [
    { label: 'Full-time', value: 'full-time' }, { label: 'Part-time', value: 'part-time' },
    { label: 'Contract', value: 'contract' }, { label: 'Intern', value: 'intern' },
  ];
  workHereOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];
  billableOptions = [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }];
  emergencyOptions = [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }];

  constructor(
    private api: ApiService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private http: HttpClient,
    private permissionService: PermissionService,
  ) {
    this.loadCountries();
  }

  openAdd(): void {
    this.reviewDocs = {};
    this.form = this.emptyForm();
    this.form.employeeId = 'Generating...';
    const tempPwd = 'Cyber@' + Math.random().toString(36).substring(2, 8);
    this.form.password = tempPwd;
    this.form.generatedPassword = tempPwd;
    this.educationRows = [this.emptyEducationRow()];
    this.experienceRows = [this.emptyExperienceRow()];
    this.dependentRows = [this.emptyDependentRow()];
    this.healthRows = [this.emptyHealthRow()];
    this.rolesRows = [this.emptyRoleRow()];
    this.pendingDocs = {};
    this.resetSubmitState();
    this.formMode = 'add';
    this.empDrawerTab = 'personal';
    this.editingId = '';
    this.formDirty = false;
    this.showFormDialog = true;
    this.formSnapshot = JSON.stringify(this.form);
    this.buildManagers();
  }

  openView(emp: any): void {
    this.reviewDocs = {};
    this.populateForm(emp);
    this.formMode = 'view';
    this.empDrawerTab = 'personal';
    this.editingId = emp.id || '';
    this.formDirty = false;
    this.resetSubmitState();
    this.showFormDialog = true;
    this.buildManagers(emp.id);
    if (this.form.id) this.loadDocuments();
  }

  openEdit(emp: any): void {
    this.reviewDocs = {};
    this.populateForm(emp);
    this.editingId = emp.id || '';
    this.formMode = 'edit';
    this.empDrawerTab = 'personal';
    this.formDirty = false;
    this.resetSubmitState();
    this.showFormDialog = true;
    this.formSnapshot = JSON.stringify(this.form);
    this.buildManagers(emp.id);
    if (this.form.id) this.loadDocuments();
  }

  openReview(record: any, docs: Record<string, string> = {}): void {
    this.reviewDocs = docs || {};
    this.populateForm(record);
    this.formMode = 'edit';
    this.empDrawerTab = 'personal';
    this.editingId = '';
    this.formDirty = false;
    this.resetSubmitState();
    this.showFormDialog = true;
    this.buildManagers();
  }

  afterSave(): void {
    this.submitting = false;
    this.showFormDialog = false;
    this.formDirty = false;
    this.empDocs = [];
    this.docUploadType = '';
    this.closed.emit();
  }
  afterError(): void { this.submitting = false; }

  emptyForm(): any {
    return {
      employeeId: '', firstName: '', middleName: '', lastName: '', nickName: '',
      email: '', officialEmail: '',
      departmentId: '', zohoRole: '', location: '', employmentType: 'full-time',
      designation: '', status: 'active', clientName: '', sourceOfHire: '',
      project: '', dateOfJoining: '', bgvStatus: '', dateOfConfirmation: '',
      currentExperience: '', totalExperience: '', noticePeriodDays: null, billable: 'No',
      bgvDate: '',
      title: '', experience: '', grade: '', currentSalary: null, salary: null,
      teamId: '', clientId: '', offerLetterUrl: '', tentativeJoiningDate: '',
      managerId: '', secondaryReportingManagerId: '', reportingTo: '',
      dateOfBirth: '', citizenship: '', gender: '', bloodGroup: '',
      maritalStatus: '', weddingDay: '', linkedinProfile: '', skillSet: '',
      certifications: '', additionalInfo: '', highestQualification: '',
      uanNumber: '', panNumber: '', aadhaarNumber: '', hasPassport: '', passportNumber: '',
      passportValidUntil: '', bankName: '', ifscCode: '', bankAccount: '', branchName: '',
      panCardUrl: '', aadhaarCardUrl: '', cancelledChequeUrl: '',
      phone: '', workPhone: '', personalMobile: '', extension: '',
      personalEmail: '', seatingLocation: '', tags: '',
      presentAddressLine1: '', presentAddressLine2: '', presentCity: '',
      presentState: '', presentCountry: '', presentPostalCode: '',
      sameAsPresentAddress: false,
      permanentAddressLine1: '', permanentAddressLine2: '', permanentCity: '',
      permanentState: '', permanentCountry: '', permanentPostalCode: '',
      lastWorkingDate: '', dateOfExit: '', exitRemarks: '',
      signedOfferLetterUrl: '', signedAppointmentLetterUrl: '', signedBondUrl: '',
      signedNdaUrl: '', bgvReportUrl: '', esicCardUrl: '', medicalAcceptanceCardUrl: '',
      role: 'employee', password: '', onboardingStatus: '',
    };
  }
  emptyEducationRow(): any { return { schoolName: '', degree: '', fieldOfStudy: '', dateOfCompletion: '', notes: '', attachmentDocId: '', attachmentName: '' }; }
  emptyExperienceRow(): any { return { occupation: '', company: '', summary: '', duration: '', currentlyWorkHere: false, attachmentDocId: '', attachmentName: '' }; }
  emptyDependentRow(): any { return { name: '', relationship: '', dateOfBirth: '', contactNo: '', emergencyContact: 'No' }; }
  emptyHealthRow(): any { return { name: '', docUrl: '', attachmentDocId: '', attachmentName: '' }; }
  emptyRoleRow(): any { return { clientId: '', clientName: '', project: '' }; }

  private populateForm(emp: any): void {
    this.form = { ...this.emptyForm(), ...emp };
    if (this.form.passportNumber && !this.form.hasPassport) this.form.hasPassport = 'yes';
    const parse = (raw: any, fallback: () => any) => {
      try { return raw ? JSON.parse(raw) : [fallback()]; } catch { return [fallback()]; }
    };
    this.educationRows = parse(emp.education, () => this.emptyEducationRow());
    this.experienceRows = parse(emp.workExperience, () => this.emptyExperienceRow());
    this.dependentRows = parse(emp.dependents, () => this.emptyDependentRow());
    this.healthRows = parse(emp.healthCare, () => this.emptyHealthRow());
    this.rolesRows = parse(emp.rolesResponsibilities, () => this.emptyRoleRow());

    if (emp.clientId && !this.rolesRows.some(r => String(r?.clientId ?? '').trim() !== '')) {
      this.rolesRows[0] = {
        ...this.rolesRows[0],
        clientId: emp.clientId,
        clientName: emp.clientName || this.getClientName(emp.clientId)
      };
    }
  }

  private resetSubmitState(): void { this.submitAttempted = false; this.missingRequired = []; this.submitting = false; }

  private buildManagers(excludeId = ''): void {
    const candidates = (this.employees || []).filter((e: any) =>
      ['ceo', 'super_admin', 'admin', 'hr_admin', 'hr', 'manager']
        .includes((e.role || '').toLowerCase().replace(/^role_/, '')));
    const list = candidates.filter(e => e.id !== excludeId);
    const currentId = this.form?.managerId;
    if (currentId && currentId !== excludeId && !list.some(e => e.id === currentId)) {
      const mgr = (this.employees || []).find(e => e.id === currentId);
      if (mgr) list.unshift(mgr);
    }
    this.managersList = list;
  }

  addEducationRow(): void { this.educationRows.push(this.emptyEducationRow()); }
  removeEducationRow(i: number): void { if (this.educationRows.length > 1) this.educationRows.splice(i, 1); }
  addExperienceRow(): void { this.experienceRows.push(this.emptyExperienceRow()); }
  removeExperienceRow(i: number): void { if (this.experienceRows.length > 1) this.experienceRows.splice(i, 1); }
  addDependentRow(): void { this.dependentRows.push(this.emptyDependentRow()); }
  removeDependentRow(i: number): void { if (this.dependentRows.length > 1) this.dependentRows.splice(i, 1); }
  addHealthRow(): void { this.healthRows.push(this.emptyHealthRow()); }
  removeHealthRow(i: number): void { if (this.healthRows.length > 1) this.healthRows.splice(i, 1); }
  addRoleRow(): void { this.rolesRows.push(this.emptyRoleRow()); }
  removeRoleRow(i: number): void {
    if (this.rolesRows.length > 1) {
      this.rolesRows.splice(i, 1);
      this.syncPrimaryClient();
    }
  }
  onRoleClientChange(row: any): void {
    row.clientName = this.getClientName(row.clientId);
    row.project = '';
    this.syncPrimaryClient();
    this.onFormChange();
  }

  private syncPrimaryClient(): void {
    const first = this.rolesRows.find(r => String(r?.clientId ?? '').trim() !== '');
    this.form.clientId = first?.clientId ?? '';
    this.form.clientName = first ? this.getClientName(first.clientId) : '';
  }

  rowDoc(row: any): any {
    if (row?.attachmentDocId) return { id: row.attachmentDocId, fileName: row.attachmentName || 'Attachment' };
    if (row?.attachmentName) return { fileName: row.attachmentName };
    return null;
  }
  onRowUpload(row: any, doc: any): void { if (!row || !doc) return; row.attachmentDocId = doc.id; row.attachmentName = doc.fileName || 'Attachment'; this.onFormChange(); }
  onRowRemove(row: any): void { if (!row) return; row.attachmentDocId = ''; row.attachmentName = ''; this.onFormChange(); }

  pendingDocs: { [docType: string]: File } = {};
  onPendingDoc(e: { docType: string; file: File | null }): void {
    if (e.file) this.pendingDocs[e.docType] = e.file; else delete this.pendingDocs[e.docType];
  }

  showClientModal = false;
  clientModalSaving = false;
  clientModalForm: any = { name: '', email: '' };
  clientModalProjects: string[] = [''];
  private clientModalTarget: any = null;

  openClientModal(row: any = null): void {
    this.clientModalTarget = row;
    this.clientModalForm = { name: '', email: '' };
    this.clientModalProjects = [''];
    this.showClientModal = true;
  }
  closeClientModal(): void { this.showClientModal = false; }
  addClientProject(): void { this.clientModalProjects.push(''); }
  trackByIndex(i: number): number { return i; }
  removeClientProject(i: number): void { if (this.clientModalProjects.length > 1) this.clientModalProjects.splice(i, 1); }

  saveNewClient(): void {
    const name = (this.clientModalForm.name || '').trim();
    const email = (this.clientModalForm.email || '').trim();
    if (!name || !email) { this.snackbar.showFailure('Client name and email are required'); return; }
    const projects = this.clientModalProjects.map(p => (p || '').trim()).filter(Boolean);
    this.clientModalSaving = true;
    this.api.post('/clients', { name, email, status: 'active', billingCurrency: 'INR', projects: JSON.stringify(projects) })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          const saved = this.extractItem(res);
          this.api.get('/clients').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((cl: any) => {
            this.clients = this.extractList(cl);
            const newId = saved?.id || this.clients.find((c: any) => c.name === name)?.id;
            if (newId) {
              if (this.clientModalTarget) { this.clientModalTarget.clientId = newId; this.onRoleClientChange(this.clientModalTarget); }
              else { this.form.clientId = newId; this.onClientChange(); }
            }
            this.clientModalSaving = false;
            this.showClientModal = false;
            this.snackbar.showSuccess('Client added');
          });
        },
        error: () => { this.clientModalSaving = false; this.snackbar.showFailure('Failed to add client'); },
      });
  }

  clientProjects(clientId: string): { label: string; value: string }[] {
    const c = this.clients.find((x: any) => x.id === clientId);
    if (!c?.projects) return [];
    try { const arr = JSON.parse(c.projects); return (Array.isArray(arr) ? arr : []).map((p: string) => ({ label: p, value: p })); }
    catch { return []; }
  }

  get filteredTeams(): any[] {
    if (!this.form.departmentId) return this.teams;
    return this.teams.filter(t => t.departmentId === this.form.departmentId);
  }
  getClientName(clientId: string): string { return clientId ? (this.clients.find((c: any) => c.id === clientId)?.name || '') : ''; }
  getDeptName(deptId: string): string { return this.departments.find(d => d.id === deptId)?.name || ''; }
  getTeamLeadName(leadId: string): string { if (!leadId) return '—'; const e = this.employees.find(emp => emp.id === leadId); return e ? `${e.firstName} ${e.lastName}` : '—'; }
  getTeamShiftName(shiftId: string): string { return shiftId ? (this.teams.find((s: any) => s.id === shiftId)?.name || 'General') : 'General'; }
  getAddressString(parts: string[]): string { return parts.filter(p => !!p).join(', ') || '—'; }

  empNameFn = (e: any) => { if (!e) return ''; const n = `${e.firstName || ''} ${e.lastName || ''}`.trim(); return n || e.employeeId || e.email || ''; };
  getManagerName(managerId: string): string {
    if (!managerId) return '';
    const mgr = this.managersList.find(m => m.id === managerId) || this.employees.find(e => e.id === managerId);
    return mgr ? this.empNameFn(mgr) : '';
  }
  getManagerDetails(managerId: string): any {
    if (!managerId) return null;
    const mgr = this.managersList.find(m => m.id === managerId) || this.employees.find(e => e.id === managerId);
    if (!mgr) return null;
    return {
      name: this.empNameFn(mgr), employeeId: mgr.employeeId || '',
      role: mgr.designation || mgr.role || 'Manager',
      photo: mgr.profilePhoto || mgr.photo || mgr.avatarUrl || null,
      initials: ((mgr.firstName?.charAt(0) || '') + (mgr.lastName?.charAt(0) || '')).toUpperCase(),
    };
  }
  getInitials(emp: any): string { return `${(emp.firstName || '?')[0]}${(emp.lastName || '?')[0]}`.toUpperCase(); }
  getAvatarColor(emp: any): string {
    const palette = ['#334155', '#475569', '#0f766e', '#166534', '#92400e', '#7c3a5a', '#3f4a5f', '#5b6472'];
    const seed = (emp.firstName || '') + (emp.lastName || '');
    let hash = 0; for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  }

  isIndian(): boolean { const c = (this.form.citizenship || '').trim().toLowerCase(); return c === '' || c === 'india' || c === 'indian'; }
  bgvAging(): string {
    if (!this.form.bgvDate) return '';
    const d = new Date(this.form.bgvDate); if (isNaN(d.getTime())) return '';
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    return days < 0 ? '' : `${days} day(s)`;
  }
  isBillable(): boolean { return (this.form.billable || '').toLowerCase() === 'yes'; }
  onBillableChange(): void { if (!this.isBillable()) { this.form.clientId = ''; this.form.clientName = ''; } this.onFormChange(); }
  onClientChange(): void { this.form.clientName = this.getClientName(this.form.clientId); this.onFormChange(); }
  isMarried(): boolean { return (this.form.maritalStatus || '').toLowerCase() === 'married'; }
  onMaritalChange(): void { if (!this.isMarried()) this.form.weddingDay = ''; this.onFormChange(); }

  private isBgvGreen(s: string): boolean {
    return /clear|verif|complet|pass|green|done|approv|positive|success/i.test(s || '');
  }
  onBgvStatusChange(): void {
    if (this.isBgvGreen(this.form.bgvStatus) && !this.form.bgvDate) {
      const d = new Date();
      this.form.bgvDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    this.onFormChange();
  }

  onPassportChange(): void {
    if (this.form.hasPassport !== 'yes') { this.form.passportNumber = ''; this.form.passportValidUntil = ''; }
    this.onFormChange();
  }
  calcAge(dob: string): string {
    if (!dob) return ''; const d = new Date(dob); if (isNaN(d.getTime())) return '';
    const now = new Date(); let years = now.getFullYear() - d.getFullYear(); let months = now.getMonth() - d.getMonth();
    if (months < 0 || (months === 0 && now.getDate() < d.getDate())) { years--; months += 12; }
    if (now.getDate() < d.getDate()) months = (months + 11) % 12;
    return years >= 0 ? `${years} year(s) ${months} month(s)` : '';
  }

  onFormChange(): void { if (this.formMode !== 'view') this.formDirty = JSON.stringify(this.form) !== this.formSnapshot; }
  onSameAddressChange(): void {
    if (this.form.sameAsPresentAddress) {
      this.form.permanentAddressLine1 = this.form.presentAddressLine1;
      this.form.permanentAddressLine2 = this.form.presentAddressLine2;
      this.form.permanentCity = this.form.presentCity;
      this.form.permanentState = this.form.presentState;
      this.form.permanentCountry = this.form.presentCountry;
      this.form.permanentPostalCode = this.form.presentPostalCode;
    }
    this.onFormChange();
  }
  onDepartmentChange(): void { if (!this.filteredTeams.some(t => t.id === this.form.teamId)) this.form.teamId = ''; this.onFormChange(); }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which || event.keyCode;
    if (charCode < 48 || charCode > 57) { event.preventDefault(); return false; }
    return true;
  }
  enforceMaxLength(event: any, field: string): void {
    const value = event.target.value.replace(/\D/g, '');
    event.target.value = value.slice(0, 10);
    this.form[field] = event.target.value;
    this.onFormChange();
  }
  isPhoneDuplicate(): boolean {
    const phone = this.form.phone?.trim();
    if (!phone || phone.length < 10) return false;
    return this.employees.some(e => e.phone === phone && e.id !== this.editingId);
  }

  private requiredFields(): { field: string; label: string; tab: string }[] {
    const base: { field: string; label: string; tab: string }[] = [
      { field: 'firstName', label: 'First Name', tab: 'personal' },
      { field: 'lastName', label: 'Last Name', tab: 'personal' },
      { field: 'email', label: 'Email address', tab: 'personal' },
      { field: 'departmentId', label: 'Department', tab: 'personal' },
      { field: 'location', label: 'Location', tab: 'personal' },
      { field: 'designation', label: 'Designation', tab: 'personal' },
      { field: 'employmentType', label: 'Employment Type', tab: 'personal' },
      { field: 'sourceOfHire', label: 'Source of Hire', tab: 'personal' },
      { field: 'bgvStatus', label: 'BGV Status', tab: 'personal' },
      { field: 'billable', label: 'Billable', tab: 'personal' },
      { field: 'managerId', label: 'Reporting Manager', tab: 'personal' },
      { field: 'dateOfBirth', label: 'Date of Birth', tab: 'professional' },
      { field: 'citizenship', label: 'Citizenship', tab: 'professional' },
      { field: 'bloodGroup', label: 'Blood Group', tab: 'professional' },
      { field: 'gender', label: 'Gender', tab: 'professional' },
      { field: 'maritalStatus', label: 'Marital Status', tab: 'professional' },
      { field: 'workPhone', label: 'Work Phone Number', tab: 'address' },
      { field: 'personalMobile', label: 'Personal Mobile Number', tab: 'address' },
      { field: 'personalEmail', label: 'Personal Email Address', tab: 'address' },
    ];
    if (this.isIndian()) {
      base.push(
        { field: 'uanNumber', label: 'UAN', tab: 'professional' },
        { field: 'panNumber', label: 'PAN', tab: 'professional' },
        { field: 'aadhaarNumber', label: 'Aadhaar', tab: 'professional' },
      );
    } else {
      base.push({ field: 'passportNumber', label: 'Passport', tab: 'professional' });
    }
    if (this.isBillable()) base.push({ field: 'rolesClient', label: 'Client Name', tab: 'personal' });
    return base;
  }
  isFieldMissing(field: string): boolean { return this.submitAttempted && this.missingRequired.includes(field); }

  private noRolesClient(): boolean {
    return !this.rolesRows.some(r => String(r?.clientId ?? '').trim() !== '');
  }

  private computeMissing(): { field: string; label: string; tab: string }[] {
    return this.requiredFields().filter(r => {
      if (r.field === 'rolesClient') return this.noRolesClient();
      const v = this.form[r.field];
      return v === null || v === undefined || String(v).trim() === '';
    });
  }

  submitEmployee(): void {
    this.submitAttempted = true;
    const missing = this.computeMissing();
    this.missingRequired = missing.map(m => m.field);
    if (missing.length) {
      this.empDrawerTab = missing[0].tab as any;
      const names = missing.slice(0, 6).map(m => m.label).join(', ');
      this.snackbar.showFailure(`Please fill ${missing.length} required field(s): ${names}${missing.length > 6 ? ` +${missing.length - 6} more` : ''}`);
      return;
    }
    if (this.form.phone && !/^\d{10}$/.test(this.form.phone)) { this.snackbar.showFailure('Phone number must be exactly 10 digits'); return; }
    if (this.isPhoneDuplicate()) { this.snackbar.showFailure('This phone number is already registered to another employee'); return; }

    const payload = { ...this.form };
    const selectedTeamId = payload.teamId;
    delete payload.employeeId;
    delete payload.teamId;
    payload.education = JSON.stringify(this.educationRows);
    payload.workExperience = JSON.stringify(this.experienceRows);
    payload.dependents = JSON.stringify(this.dependentRows);
    payload.healthCare = JSON.stringify(this.healthRows);
    payload.rolesResponsibilities = JSON.stringify(this.rolesRows);

    this.submitting = true;
    this.submitForm.emit({
      payload, mode: this.formMode, editingId: this.editingId,
      selectedTeamId, pendingDocs: this.pendingDocs, formId: this.form.id,
    });
  }

  async closeDialog(): Promise<void> {
    if (this.formDirty) {
      const ok = await this.confirmDialog.confirm('You have unsaved changes. Are you sure you want to close?', {
        title: 'Unsaved Changes', confirmLabel: 'Discard', cancelLabel: 'Keep Editing', danger: true,
      });
      if (!ok) return;
    }
    this.showFormDialog = false;
    this.formDirty = false;
    this.empDocs = [];
    this.docUploadType = '';
    this.closed.emit();
  }

  canEditEmployee(): boolean { return this.permissionService.canEditInModule('employees'); }

  loadCountries(): void {
    this.http.get<any>('https://countriesnow.space/api/v0.1/countries').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => { if (res?.data) this.countries = res.data.map((c: any) => c.country).sort(); },
      error: () => { this.countries = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia']; },
    });
  }
  onPresentCountryChange(): void {
    this.presentStates = []; this.presentCities = []; this.form.presentState = ''; this.form.presentCity = '';
    if (!this.form.presentCountry) return;
    this.http.post<any>('https://countriesnow.space/api/v0.1/countries/states', { country: this.form.presentCountry })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (res) => { if (res?.data?.states) this.presentStates = res.data.states.map((s: any) => s.name).sort(); } });
    this.onFormChange();
  }
  onPresentStateChange(): void {
    this.presentCities = []; this.form.presentCity = '';
    if (!this.form.presentState) return;
    this.http.post<any>('https://countriesnow.space/api/v0.1/countries/state/cities', { country: this.form.presentCountry, state: this.form.presentState })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (res) => { if (res?.data) this.presentCities = res.data.sort(); } });
    this.onFormChange();
  }
  onPermanentCountryChange(): void {
    this.permanentStates = []; this.permanentCities = []; this.form.permanentState = ''; this.form.permanentCity = '';
    if (!this.form.permanentCountry) return;
    this.http.post<any>('https://countriesnow.space/api/v0.1/countries/states', { country: this.form.permanentCountry })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (res) => { if (res?.data?.states) this.permanentStates = res.data.states.map((s: any) => s.name).sort(); } });
    this.onFormChange();
  }
  onPermanentStateChange(): void {
    this.permanentCities = []; this.form.permanentCity = '';
    if (!this.form.permanentState) return;
    this.http.post<any>('https://countriesnow.space/api/v0.1/countries/state/cities', { country: this.form.permanentCountry, state: this.form.permanentState })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (res) => { if (res?.data) this.permanentCities = res.data.sort(); } });
    this.onFormChange();
  }
  onPresentPostalChange(): void {
    this.onFormChange();
    const code = (this.form.presentPostalCode || '').trim();
    if (code.length < 5) { this.postalLookupError.present = ''; return; }
    this.lookupPostalCode(code, 'present');
  }
  onPermanentPostalChange(): void {
    this.onFormChange();
    const code = (this.form.permanentPostalCode || '').trim();
    if (code.length < 5) { this.postalLookupError.permanent = ''; return; }
    this.lookupPostalCode(code, 'permanent');
  }
  private lookupPostalCode(code: string, type: 'present' | 'permanent'): void {
    this.postalLookupLoading[type] = true; this.postalLookupError[type] = '';
    const countryField = type === 'present' ? this.form.presentCountry : this.form.permanentCountry;
    const countryParam = countryField ? `&countrycodes=${this.countryNameToCode(countryField) || 'in'}` : '';
    const url = `https://nominatim.openstreetmap.org/search?postalcode=${code}&format=json&addressdetails=1&limit=1${countryParam}`;
    this.http.get<any[]>(url, { headers: { 'Accept-Language': 'en' } }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.postalLookupLoading[type] = false;
        if (res?.length) {
          const a = res[0].address || {};
          this.applyPostalResult(type, a.country || countryField || '', a.state || '', a.city || a.town || a.village || a.county || '');
        } else { this.postalLookupError[type] = 'PIN code not found'; }
      },
      error: () => { this.postalLookupLoading[type] = false; this.postalLookupError[type] = 'Lookup failed'; },
    });
  }
  private applyPostalResult(type: 'present' | 'permanent', country: string, state: string, city: string): void {
    if (type === 'present') {
      if (country) { this.form.presentCountry = country; this.onPresentCountryChange(); }
      setTimeout(() => { if (state) { this.form.presentState = state; this.onPresentStateChange(); } setTimeout(() => { if (city) this.form.presentCity = city; }, 600); }, 600);
    } else {
      if (country) { this.form.permanentCountry = country; this.onPermanentCountryChange(); }
      setTimeout(() => { if (state) { this.form.permanentState = state; this.onPermanentStateChange(); } setTimeout(() => { if (city) this.form.permanentCity = city; }, 600); }, 600);
    }
  }
  private countryNameToCode(name: string): string {
    const map: { [k: string]: string } = { 'india': 'in', 'united states': 'us', 'united kingdom': 'gb', 'canada': 'ca', 'australia': 'au', 'germany': 'de', 'france': 'fr', 'singapore': 'sg', 'uae': 'ae' };
    return map[(name || '').toLowerCase()] || '';
  }

  private DOC_TYPE_MAP: Record<string, string> = {
    PAN_CARD: 'pan', AADHAAR_CARD: 'aadhaar', CANCELLED_CHEQUE: 'cancelled_cheque', ESIC_CARD: 'esic_card', MEDICAL_ACCEPTANCE: 'medical_acceptance',
  };
  docCanonicalType(t: string): string { return this.DOC_TYPE_MAP[(t || '').toUpperCase()] || (t || '').toLowerCase(); }
  get importedDocs(): any[] {
    const raw = this.form?.documents;
    if (!raw) return [];
    let list: any[];
    try { list = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return []; }
    if (!Array.isArray(list)) return [];
    const uploaded = new Set((this.empDocs || []).map((d: any) => (d.documentType || '').toLowerCase()));
    return list.filter(d => d && !uploaded.has(this.docCanonicalType(d.type)));
  }
  loadDocuments(): void {
    if (!this.form.id) return;
    this.docsLoading = true;
    this.api.get(`/employee-documents/employee/${this.form.id}`).pipe(catchError(() => of({ data: [] }))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.empDocs = Array.isArray(res) ? res : (res?.data ?? []);
      this.docsLoading = false;
    });
  }
  onDocFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.docUploadType || !this.form.id) return;
    const file = input.files[0]; input.value = '';
    const formData = new FormData();
    formData.append('file', file); formData.append('documentType', this.docUploadType);
    this.docUploading = true;
    this.api.postForm(`/employee-documents/employee/${this.form.id}/upload`, formData).pipe(
      catchError((err: any) => { this.snackbar.showFailure(err?.error?.message || 'Upload failed'); return of(null); })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.docUploading = false;
      if (res?.success || res?.data) { this.snackbar.showSuccess('Document uploaded'); this.docUploadType = ''; this.loadDocuments(); }
    });
  }
  downloadDoc(doc: any): void {
    this.api.get(`/employee-documents/${doc.id}/download`).pipe(catchError(() => { this.snackbar.showFailure('Download failed'); return of(null); }))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
        if (!res?.fileContent) return;
        const byteStr = atob(res.fileContent);
        const bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
        const blob = new Blob([bytes], { type: res.mimeType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = res.fileName || doc.fileName; a.click();
        URL.revokeObjectURL(url);
      });
  }
  async deleteDoc(docId: string): Promise<void> {
    const ok = await this.confirmDialog.confirm('Delete this document? This cannot be undone.', { title: 'Delete Document', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    this.api.delete(`/employee-documents/${docId}`).pipe(catchError(() => { this.snackbar.showFailure('Delete failed'); return of(null); }))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => { if (res?.success) { this.snackbar.showSuccess('Document deleted'); this.loadDocuments(); } });
  }
  findDoc(type: string): any {
    const real = (this.empDocs || []).find((d: any) => d.documentType === type);
    if (real) return real;
    const matched = this.reviewDocs[type];
    return matched ? { fileName: matched, documentType: type } : null;
  }
  docTypeLabel(type: string): string {
    const base = (type || '').replace(/_\d{6,}$/, '');
    const map: any = {
      aadhaar: 'Aadhaar', pan: 'PAN', photo: 'Photo', passport: 'Passport',
      cancelled_cheque: 'Cancelled Cheque', esic_card: 'ESIC Card', medical_acceptance: 'Medical Acceptance',
      offer_letter: 'Offer Letter', appointment_letter: 'Appointment Letter', bond: 'Bond', nda: 'NDA', bgv_report: 'BGV Report',
      education_cert: 'Education', education_attachment: 'Education',
      experience_letter: 'Experience', experience_attachment: 'Experience',
      health_doc: 'Health Care', healthcare_attachment: 'Health Care', other: 'Other',
    };
    return map[base] || base.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  }
  getDocIcon(mime: string): string {
    if (!mime) return 'insert_drive_file';
    if (mime.includes('pdf')) return 'picture_as_pdf';
    if (mime.includes('image')) return 'image';
    if (mime.includes('word') || mime.includes('document')) return 'description';
    return 'insert_drive_file';
  }
  getDocIconClass(mime: string): string {
    if (!mime) return 'ed-doc-icon--file';
    if (mime.includes('pdf')) return 'ed-doc-icon--pdf';
    if (mime.includes('image')) return 'ed-doc-icon--img';
    return 'ed-doc-icon--file';
  }
  formatFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }
  isExpired(dateStr: string): boolean { return dateStr ? new Date(dateStr) < new Date() : false; }

  private extractList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }
  private extractItem(response: any): any {
    if (response?.data && !Array.isArray(response.data)) return response.data;
    return response;
  }
}
